/**
 * @file TwoLineCell.tsx
 * @description Двухстрочная ячейка «основное + подпись» (phase19.31, шаг О4):
 * «Z-2026-000031 / PICK-…000031», «Основной склад / Самовывоз».
 *
 * Основная строка — текст 14/400 (`type-body`), подпись — 12/400 вторым
 * цветом (`type-caption`). Моноширинный шрифт — только по флагу `mono` и
 * только для кодов (номера документов, артикулы, SC43). Обе строки режутся
 * многоточием, полный текст — в подсказке. `wrapPrimary` — основная строка
 * переносится до двух строк (длинные названия сделок, QA-5 D4).
 *
 * ```tsx
 * <TwoLineCell primary={task.orderNumber} secondary={task.number} mono />
 * ```
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface Props {
  primary: ReactNode;
  secondary?: ReactNode;
  /** Код (номер документа, артикул) — моноширинным. */
  mono?: boolean;
  className?: string;
  /** Основная строка — до двух строк вместо многоточия. */
  wrapPrimary?: boolean;
}

/** Текст подсказки: только если содержимое — строка. */
function hint(value: ReactNode): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export function TwoLineCell({ primary, secondary, mono = false, className, wrapPrimary = false }: Props) {
  return (
    <span className={cn("flex min-w-0 flex-col", className)}>
      <span title={hint(primary)} className={cn("type-body text-on-surface", wrapPrimary ? "line-clamp-2 break-words" : "truncate", mono && "font-mono")}>
        {primary}
      </span>
      {secondary ? (
        <span
          title={hint(secondary)}
          className={cn("type-caption truncate text-on-surface-variant", mono && "font-mono")}
        >
          {secondary}
        </span>
      ) : null}
    </span>
  );
}
